// Authentication Utility Functions
// File: src/utils/authUtils.js

import { auth, googleProvider, signInWithPopup, signOut, onAuthStateChanged } from '../firebase'

/**
 * Sign in with Google
 * @returns {Promise<{success: boolean, user?: object, error?: string}>}
 */
export async function signInWithGoogle () {
  try {
    const result = await signInWithPopup(auth, googleProvider)

    console.log('✓ User signed in:', result.user.displayName || result.user.email)

    return {
      success: true,
      user: {
        uid: result.user.uid,
        displayName: result.user.displayName,
        email: result.user.email,
        photoURL: result.user.photoURL
      }
    }
  } catch (error) {
    console.error('Sign-in error:', error)

    // Handle specific error codes
    let errorMessage = 'Sign-in failed. Please try again.'

    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'Sign-in cancelled.'
    } else if (error.code === 'auth/popup-blocked') {
      errorMessage = 'Pop-up blocked. Please allow pop-ups for this site.'
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your connection.'
    }

    return {
      success: false,
      error: errorMessage
    }
  }
}

/**
 * Sign out the current user
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function signOutUser () {
  try {
    await signOut(auth)
    console.log('✓ User signed out')

    return { success: true }
  } catch (error) {
    console.error('Sign-out error:', error)

    return {
      success: false,
      error: 'Sign-out failed. Please try again.'
    }
  }
}

/**
 * Get the current user
 * @returns {object|null} Current user object or null if not signed in
 */
export function getCurrentUser () {
  return auth.currentUser
}

/**
 * Get the current user's ID (Firebase UID)
 * @returns {string|null} User ID or null if not signed in
 */
export function getUserId () {
  const user = auth.currentUser
  return user ? user.uid : null
}

/**
 * Get the current user's ID token (for API authentication)
 * @param {boolean} forceRefresh - Force refresh the token
 * @returns {Promise<string|null>} ID token or null if not signed in
 */
export async function getUserIdToken (forceRefresh = false) {
  const user = auth.currentUser
  if (!user) {
    return null
  }

  try {
    const token = await user.getIdToken(forceRefresh)
    return token
  } catch (error) {
    console.error('Error getting ID token:', error)
    return null
  }
}

/**
 * Check if user is currently signed in
 * @returns {boolean} True if signed in, false otherwise
 */
export function isSignedIn () {
  return !!auth.currentUser
}

/**
 * Listen for authentication state changes
 * @param {Function} callback - Called with user object (or null) when auth state changes
 * @returns {Function} Unsubscribe function
 */
export function onAuthChange (callback) {
  return onAuthStateChanged(auth, (user) => {
    callback(user)
  })
}

/**
 * Get user info for display
 * @returns {object|null} User info or null
 */
export function getUserInfo () {
  const user = auth.currentUser

  if (!user) {
    return null
  }

  return {
    uid: user.uid,
    displayName: user.displayName || 'User',
    email: user.email,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    isAnonymous: user.isAnonymous
  }
}

/**
 * Wait for auth to be ready (useful for initial load)
 * @returns {Promise<object|null>} Current user or null
 */
export function waitForAuth () {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe()
      resolve(user)
    })
  })
}
